/**
 * Identity header of the Inventar detail page — Inventar 2.0, slice S6b.
 *
 * Replaces a full-bleed dark bar that held a back link and two buttons, with
 * the article's name and number stranded in a separate block below it. The
 * name is now the h1 it always should have been, and everything that
 * identifies the article — number, category, brand, status — reads as one
 * line beneath it instead of three competing chips.
 *
 * TOKENS ONLY. The old header was painted with hex values and patched back to
 * something readable in light mode by an !important block in index.css. It now
 * inherits the theme like every other modernised page.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { inventoryStatusLabelDe } from "../../../utils/inventoryExport";
import { inventoryStatusToneClass } from "../../../utils/inventoryStatus";

/**
 * Status as a badge. Reads the SAME label and tone maps as the overview row,
 * which is the whole point: before S6b the detail page had its own map that
 * lacked "retired" and showed the raw English word.
 *
 * The label is never colour alone — the word carries the meaning, the tone
 * only reinforces it.
 */
export function InventoryStatusBadge({ status, className = "" }) {
  const label = inventoryStatusLabelDe(status);
  if (!label) return null;
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${inventoryStatusToneClass(
        status
      )} ${className}`}
    >
      {label}
    </span>
  );
}

/**
 * Number · category · brand, skipping whatever the article does not have.
 *
 * S7c: these read as badges now instead of a dot-separated sentence, which is
 * how the reference groups them next to the title. Same three values, same
 * order, same omission rule — only the chrome changed. The inventory number
 * keeps its monospace so it stays scannable against the other two.
 */
function IdentityMeta({ item }) {
  const parts = [item.inventory_number, item.category, item.brand].filter(
    (p) => p != null && String(p).trim() !== ""
  );
  if (parts.length === 0) return null;
  return (
    <p className="m-0 flex flex-wrap items-center gap-2">
      {parts.map((part, i) => (
        <span
          key={`${part}-${i}`}
          className={`inline-flex whitespace-nowrap rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground ${
            i === 0 ? "font-mono" : ""
          }`}
        >
          {part}
        </span>
      ))}
    </p>
  );
}

/**
 * The article's own picture, beside its name.
 *
 * It is the FIRST entry of the gallery the page has already fetched — no extra
 * request, no original_url, the same thumb_url the overview row shows as
 * primary_thumb_url. Renders nothing at all when the article has no photo, so
 * the identity zone does not carry an empty placeholder box.
 */
function IdentityThumb({ url, name }) {
  const src = String(url || "").trim();
  if (!src) return null;
  return (
    <span className="hidden h-[52px] w-[52px] shrink-0 overflow-hidden rounded-[10px] border border-border bg-muted sm:block">
      <img src={src} alt="" aria-hidden className="h-full w-full object-cover" title={name} />
    </span>
  );
}

export default function InventoryDetailHeader({ item, thumbUrl, onEdit, onDelete }) {
  return (
    <header className="flex flex-col gap-3">
      <Link
        to="/admin/inventory"
        className="inline-flex w-fit items-center gap-1.5 rounded-[6px] text-[12.5px] font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Zurück zum Inventar
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {/* S7c identity zone: picture, name and badges are ONE cluster, the way
            the reference groups them. The photo used to live in a card far
            below the title, which made the two read as unrelated. */}
        <div className="flex min-w-0 items-start gap-3.5">
          <IdentityThumb url={thumbUrl} name={item.name} />
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="m-0 text-[21px] font-bold leading-tight tracking-tight text-foreground">
                {item.name}
              </h1>
              <InventoryStatusBadge status={item.status} />
            </div>
            <IdentityMeta item={item} />
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-[8px] border border-border bg-card px-3.5 py-2 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Bearbeiten
          </button>
          {/* Destructive, so it reads as destructive — but it stays an outline,
              not a filled red slab competing with the primary action. */}
          <button
            type="button"
            onClick={onDelete}
            className="rounded-[8px] border border-destructive/40 bg-transparent px-3.5 py-2 text-[12.5px] font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Löschen
          </button>
        </div>
      </div>
    </header>
  );
}
